import React, { createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAutoModalSuspension } from '../../common/AutoModalProvider';
import useGetCompanyVerificationInfo from '../../common/hooks/useGetCompanyVerificationInfo';
import {
  CompanyVerificationRequest,
  VerificationMethod,
} from '../../common/interfaces/entities';
import { getSessionCompanyCountryCode } from '../../selectors/session';
import { trackCompanyVerificationViewedEvent } from '../Modal/CompanyTierVerificationModal/hooks/useCompanyVerificationTracking';

type CompanyVerificationModalsContext = {
  isVerificationModalOpen: boolean;
  isRejectionModalClosed: boolean;
  isSuccessModalClosed: boolean;
  lastVerificationRequest: CompanyVerificationRequest | null;
  preSelectedVerificationMethod: VerificationMethod | null;
  openVerificationModal: () => void;
  openVerificationModalWithMethod: (
    verificationMethod: VerificationMethod
  ) => void;
  closeVerificationModal: () => void;
  setSubmittedModalOpen: (open: boolean) => void;
  openRejectionModal: (options?: {
    lastVerificationRequest?: CompanyVerificationRequest;
  }) => void;
  closeRejectionModal: () => void;
  openSuccessModal: (options?: {
    lastVerificationRequest?: CompanyVerificationRequest;
  }) => void;
  closeSuccessModal: () => void;
};

const CompanyVerificationModalsContext =
  createContext<CompanyVerificationModalsContext | null>(null);

export const useCompanyVerificationModalsContext = () => {
  const context = useContext(CompanyVerificationModalsContext);
  if (!context) {
    throw new Error(
      'useCompanyVerificationModalsContext must be used within CompanyVerificationModalsProvider'
    );
  }
  return context;
};

export const CompanyVerificationModalsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { verificationInfo, isCompanyVerificationInProgress } =
    useGetCompanyVerificationInfo();
  const [isSubmittedModalOpen, setSubmittedModalOpen] = useState(false);
  const [
    {
      isRejectionModalClosed,
      isVerificationModalOpen,
      isSuccessModalClosed,
      lastVerificationRequest,
      preSelectedVerificationMethod,
    },
    setState,
  ] = useState<{
    isRejectionModalClosed: boolean;
    isSuccessModalClosed: boolean;
    isVerificationModalOpen: boolean;
    lastVerificationRequest: CompanyVerificationRequest | null;
    preSelectedVerificationMethod: VerificationMethod | null;
  }>({
    isRejectionModalClosed: false,
    isSuccessModalClosed: false,
    isVerificationModalOpen: false,
    lastVerificationRequest: verificationInfo?.lastVerificationRequest || null,
    preSelectedVerificationMethod: null,
  });

  const countryCode = useSelector(getSessionCompanyCountryCode);
  useAutoModalSuspension(isVerificationModalOpen || isSubmittedModalOpen);
  const trackViewed = (verificationMethod?: VerificationMethod) =>
    trackCompanyVerificationViewedEvent(
      verificationInfo?.lastVerificationRequest?.company?.status,
      isCompanyVerificationInProgress,
      countryCode,
      verificationMethod
    );

  const [
    openVerificationModal,
    openVerificationModalWithMethod,
    closeVerificationModal,
    openRejectionModal,
    closeRejectionModal,
    openSuccessModal,
    closeSuccessModal,
  ] = [
    () => {
      setState(prevState => ({
        ...prevState,
        isVerificationModalOpen: true,
        preSelectedVerificationMethod: null,
      }));
      trackViewed();
    },
    (verificationMethod: VerificationMethod) => {
      setState(prevState => ({
        ...prevState,
        isVerificationModalOpen: true,
        preSelectedVerificationMethod: verificationMethod,
      }));
      trackViewed(verificationMethod);
    },
    () => {
      setState(prevState => ({
        ...prevState,
        isRejectionModalClosed: true,
        isVerificationModalOpen: false,
        preSelectedVerificationMethod: null,
      }));
    },
    (options?: { lastVerificationRequest?: CompanyVerificationRequest }) => {
      setState(prevState => ({
        ...prevState,
        isRejectionModalClosed: false,
        ...(options?.lastVerificationRequest && {
          lastVerificationRequest: options.lastVerificationRequest,
        }),
      }));
      trackViewed(
        options?.lastVerificationRequest?.companyVerificationMethod
          ?.verificationMethod
      );
    },
    () => {
      setState(prevState => ({
        ...prevState,
        isRejectionModalClosed: true,
      }));
    },
    (options?: { lastVerificationRequest?: CompanyVerificationRequest }) => {
      setState(prevState => ({
        ...prevState,
        isSuccessModalClosed: false,
        ...(options?.lastVerificationRequest && {
          lastVerificationRequest: options.lastVerificationRequest,
        }),
      }));
      trackViewed(
        options?.lastVerificationRequest?.companyVerificationMethod
          ?.verificationMethod
      );
    },
    () => {
      setState(prevState => ({
        ...prevState,
        isSuccessModalClosed: true,
      }));
    },
  ];

  return (
    <CompanyVerificationModalsContext.Provider
      value={{
        isVerificationModalOpen,
        isRejectionModalClosed,
        isSuccessModalClosed,
        lastVerificationRequest,
        preSelectedVerificationMethod,
        openVerificationModal,
        openVerificationModalWithMethod,
        closeVerificationModal,
        setSubmittedModalOpen,
        openRejectionModal,
        closeRejectionModal,
        openSuccessModal,
        closeSuccessModal,
      }}
    >
      {children}
    </CompanyVerificationModalsContext.Provider>
  );
};
